import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  LinearProgress,
  Pagination,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Snackbar,
  alpha,
} from '@mui/material';
import {
  Security,
  Refresh,
  DeleteSweep,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import '../../../styles/swal-overrides.css';
import {
  ConnectionService,
  GetTentativasParams,
  RadPostAuthRecord,
} from '../../../services/connectionService';
import SysDateRangePicker from '../../../components/SysDateRangePicker';
import { formatarDataHoraSegundos } from '../../../utils/formatToView';

const getCurrentMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const toYmd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { startDate: toYmd(first), endDate: toYmd(last) };
};

const parseYmdToLocalDate = (str: string): Date => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const dateToYmd = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getReplyLabel = (reply: string): string => {
  if (!reply) return 'N/A';
  if (reply.toLowerCase().includes('accept')) return 'Aceito';
  if (reply.toLowerCase().includes('reject')) return 'Rejeitado';
  return reply;
};

const getReplyColor = (reply: string): 'success' | 'error' | 'default' => {
  if (!reply) return 'default';
  if (reply.toLowerCase().includes('accept')) return 'success';
  if (reply.toLowerCase().includes('reject')) return 'error';
  return 'default';
};

interface TentativasTabProps {
  embedded?: boolean;
}

const TentativasTab: React.FC<TentativasTabProps> = ({ embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accentColor = embedded ? theme.palette.success.main : theme.palette.primary.main;

  const [tentativas, setTentativas] = useState<RadPostAuthRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTentativas, setTotalTentativas] = useState(0);

  const currentMonth = getCurrentMonthRange();

  const [startDate, setStartDate] = useState(currentMonth.startDate);
  const [endDate, setEndDate] = useState(currentMonth.endDate);
  const [usernameFilter, setUsernameFilter] = useState('');
  const [replyFilter, setReplyFilter] = useState<string>('all');

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const loadTentativas = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: GetTentativasParams = {
        page,
        limit: 20,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        username: usernameFilter.trim() || undefined,
        reply: replyFilter !== 'all' ? replyFilter : undefined,
      };

      const response = await ConnectionService.getTentativas(params);

      if (response.success) {
        setTentativas(response.data);
        setTotalPages(response.pagination.pages);
        setTotalTentativas(response.pagination.total);
      } else {
        setError('Erro ao carregar tentativas');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(`Erro ao carregar tentativas: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTentativas();
  }, [page, replyFilter]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const applyFilters = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        setToastMessage('Data de início não pode ser maior que data fim');
        setToastOpen(true);
        return;
      }
    }

    setPage(1);
    loadTentativas();
  };

  const clearFilters = () => {
    const range = getCurrentMonthRange();
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setUsernameFilter('');
    setReplyFilter('all');
    setPage(1);
    setError(null);
    setTimeout(() => {
      loadTentativas();
    }, 100);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const handleClearTable = async () => {
    const result = await Swal.fire({
      title: 'Limpar tabela',
      text: 'Tem certeza que deseja excluir TODOS os registros da tabela de tentativas? Esta ação não pode ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, limpar tabela',
      cancelButtonText: 'Cancelar',
      customClass: { container: 'swal-zindex' },
    });

    if (!result.isConfirmed) return;

    try {
      const response = await ConnectionService.clearTentativas();

      if (response.success) {
        await Swal.fire({
          title: 'Sucesso',
          text: response.message,
          icon: 'success',
          customClass: { container: 'swal-zindex' },
        });
        loadTentativas();
      } else {
        await Swal.fire({
          title: 'Erro',
          text: response.message,
          icon: 'error',
          customClass: { container: 'swal-zindex' },
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao limpar tabela';
      await Swal.fire({
        title: 'Erro',
        text: msg,
        icon: 'error',
        customClass: { container: 'swal-zindex' },
      });
    }
  };

  if (loading && tentativas.length === 0) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Carregando tentativas...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filtros */}
      <Paper elevation={2} sx={{ mb: embedded ? 1 : 1, p: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Grid container spacing={embedded ? 1 : 2} alignItems="center" justifyContent="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Usuário"
              value={usernameFilter}
              onChange={(e) => setUsernameFilter(e.target.value)}
              placeholder="Filtrar por usuário"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Resultado</InputLabel>
              <Select
                value={replyFilter}
                onChange={(e) => setReplyFilter(e.target.value)}
                label="Resultado"
                size="small"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="Access-Accept">Aceito</MenuItem>
                <MenuItem value="Access-Reject">Rejeitado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SysDateRangePicker
              value={{
                startDate: startDate ? parseYmdToLocalDate(startDate) : null,
                endDate: endDate ? parseYmdToLocalDate(endDate) : null,
              }}
              onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                setStartDate(dateToYmd(range.startDate));
                setEndDate(dateToYmd(range.endDate));
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" gap={embedded ? 0.5 : 1}>
              <Button
                variant="contained"
                onClick={applyFilters}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Aplicar
              </Button>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Limpar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de Tentativas */}
      {embedded ? (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Tentativas ({totalTentativas})
            </Typography>
            <Box display="flex" gap={0.5}>
              <Tooltip title="Limpar tabela (excluir todos os registros)">
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteSweep />}
                  onClick={handleClearTable}
                  disabled={loading}
                >
                  Limpar tabela
                </Button>
              </Tooltip>
              <Tooltip title="Atualizar">
                <IconButton onClick={loadTentativas} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {tentativas.length === 0 ? (
            <Box textAlign="center" py={2} sx={{ px: 1.5, pb: 1.5 }}>
              <Security sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhuma tentativa encontrada
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {startDate || endDate || usernameFilter || replyFilter !== 'all'
                  ? 'Nenhuma tentativa encontrada com os filtros aplicados'
                  : 'Não há registros de tentativas no período selecionado'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 380 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) => `color-mix(in srgb, ${accentColor} 12%, ${t.palette.background.paper})`,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        py: 0.5,
                        borderBottom: `2px solid ${alpha(accentColor, 0.2)}`,
                      },
                    }}>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Senha</TableCell>
                      <TableCell>Resultado</TableCell>
                      <TableCell>Data/Hora</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tentativas.map((tentativa) => (
                      <TableRow key={tentativa.id} hover sx={{ '&:hover': { bgcolor: alpha(accentColor, 0.03) } }}>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{tentativa.username || 'N/A'}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{tentativa.pass || 'N/A'}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip
                            label={getReplyLabel(tentativa.reply)}
                            color={getReplyColor(tentativa.reply)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {tentativa.authdate
                            ? formatarDataHoraSegundos(tentativa.authdate)
                            : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    bgcolor: alpha(accentColor, 0.04),
                    borderTop: `2px solid ${alpha(accentColor, 0.2)}`,
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Box>
              )}
            </>
          )}
        </Paper>
      ) : (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Tentativas ({totalTentativas})
              </Typography>
              <Box display="flex" gap={0.5}>
                <Tooltip title="Limpar tabela (excluir todos os registros)">
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteSweep />}
                    onClick={handleClearTable}
                    disabled={loading}
                  >
                    Limpar tabela
                  </Button>
                </Tooltip>
                <Tooltip title="Atualizar">
                  <IconButton onClick={loadTentativas} disabled={loading}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {tentativas.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Security sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhuma tentativa encontrada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {startDate || endDate || usernameFilter || replyFilter !== 'all'
                    ? 'Nenhuma tentativa encontrada com os filtros aplicados'
                    : 'Não há registros de tentativas no período selecionado'}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size={isMobile ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Usuário</TableCell>
                        <TableCell>Senha</TableCell>
                        <TableCell>Resultado</TableCell>
                        <TableCell>Data/Hora</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tentativas.map((tentativa) => (
                        <TableRow key={tentativa.id} hover sx={{ '&:hover': { bgcolor: alpha(accentColor, 0.03) } }}>
                          <TableCell>{tentativa.username || 'N/A'}</TableCell>
                          <TableCell>{tentativa.pass || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={getReplyLabel(tentativa.reply)}
                              color={getReplyColor(tentativa.reply)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {tentativa.authdate
                              ? formatarDataHoraSegundos(tentativa.authdate)
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: 2,
                      py: 1.5,
                      px: 2,
                      bgcolor: alpha(accentColor, 0.04),
                      borderTop: `2px solid ${alpha(accentColor, 0.2)}`,
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size={isMobile ? 'small' : 'medium'}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity="error" sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TentativasTab;
